import { useState, useEffect } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  Bars3Icon,
  ArrowUpCircleIcon,
  ArrowDownCircleIcon,
  CalculatorIcon,
  DocumentTextIcon,
  ArrowsUpDownIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

const DREEstrutura = () => {
  const [estrutura, setEstrutura] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [carregandoModeloPadrao, setCarregandoModeloPadrao] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => {
    carregarEstrutura();
  }, []);

  const carregarEstrutura = async () => {
    setCarregando(true);
    try {
      const response = await axios.get('/api/dre-estrutura');
      // Ordenar por ordem
      const sorted = response.data.sort((a, b) => a.ordem - b.ordem);
      setEstrutura(sorted);
    } catch (error) {
      console.error('Erro ao carregar estrutura DRE:', error);
      toast.error('Erro ao carregar estrutura DRE');
    } finally {
      setCarregando(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      if (editingId) {
        await axios.put(`/api/dre-estrutura/${editingId}`, data);
        toast.success('Item atualizado com sucesso!');
      } else {
        await axios.post('/api/dre-estrutura', data);
        toast.success('Item cadastrado com sucesso!');
      }
      
      setShowModal(false);
      reset();
      setEditingId(null);
      carregarEstrutura();
    } catch (error) {
      console.error('Erro ao salvar item:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar item');
    }
  };

  const handleEdit = (item) => {
    reset(item);
    setEditingId(item.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este item da estrutura DRE?')) {
      return;
    }

    try {
      await axios.delete(`/api/dre-estrutura/${id}`);
      toast.success('Item excluído com sucesso!');
      carregarEstrutura();
    } catch (error) {
      console.error('Erro ao excluir item:', error);
      toast.error('Erro ao excluir item');
    }
  };

  const moverItem = async (id, direcao) => {
    try {
      const currentIndex = estrutura.findIndex((i) => i.id === id);
      if (currentIndex === -1) return;

      const targetIndex = direcao === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= estrutura.length) return;

      const novaEstrutura = [...estrutura];
      const [item] = novaEstrutura.splice(currentIndex, 1);
      novaEstrutura.splice(targetIndex, 0, item);

      const payloads = novaEstrutura.map((row, idx) => ({
        id: row.id,
        ordem: idx + 1,
      }));

      await Promise.all(payloads.map((p) => axios.put(`/api/dre-estrutura/${p.id}`, { ordem: p.ordem })));
      toast.success('Posição atualizada!');
      carregarEstrutura();
    } catch (error) {
      console.error('Erro ao mover item:', error);
      toast.error('Erro ao mover item');
    }
  };

  const carregarModeloPadrao = async () => {
    const hasExistingStructure = estrutura.length > 0;

    if (
      hasExistingStructure &&
      !window.confirm('A estrutura atual sera substituida pelo modelo padrao. Deseja continuar?')
    ) {
      return;
    }

    try {
      setCarregandoModeloPadrao(true);
      const response = await axios.post('/api/dre-estrutura/carregar-modelo-padrao', {
        replace_existing: hasExistingStructure,
      });
      const insertedCount = Number(response.data?.inserted_count || 0);
      toast.success(
        hasExistingStructure
          ? `Modelo padrao carregado com sucesso. ${insertedCount} itens substituidos.`
          : `Modelo padrao carregado com sucesso. ${insertedCount} itens inseridos.`
      );
      await carregarEstrutura();
    } catch (error) {
      console.error('Erro ao carregar modelo padrao da DRE:', error);
      toast.error(error.response?.data?.error || 'Erro ao carregar modelo padrao');
    } finally {
      setCarregandoModeloPadrao(false);
    }
  };

  const getIconByTipo = (tipo) => {
    switch (tipo) {
      case 'receita':
        return <ArrowUpCircleIcon className="h-5 w-5 text-success-600" />;
      case 'despesa':
        return <ArrowDownCircleIcon className="h-5 w-5 text-danger-600" />;
      case 'resultado':
        return <CalculatorIcon className="h-5 w-5 text-primary-600" />;
      default:
        return <DocumentTextIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getBadgeColorByTipo = (tipo) => {
    switch (tipo) {
      case 'receita':
        return 'badge-success';
      case 'despesa':
        return 'badge-danger';
      case 'resultado':
        return 'badge-primary';
      default:
        return 'badge-info';
    }
  };

  const getTipoLabel = (tipo) => {
    switch (tipo) {
      case 'receita':
        return 'Receita';
      case 'despesa':
        return 'Despesa';
      case 'resultado':
        return 'Resultado';
      default:
        return tipo;
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">📊 Estrutura da DRE</h1>
          <p className="text-gray-600">Configure a estrutura da Demonstração do Resultado do Exercício</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={carregarModeloPadrao}
            className="btn-secondary"
            disabled={carregandoModeloPadrao}
          >
            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
            {carregandoModeloPadrao ? 'Carregando...' : 'Carregar Modelo Padrao'}
          </button>
          <button
            onClick={() => {
              reset({
                ordem: estrutura.length + 1,
                tipo: 'resultado'
              });
              setEditingId(null);
              setShowModal(true);
            }}
            className="btn-primary"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Novo Item
          </button>
        </div>
      </div>

      {/* Informações */}
      <div className="card bg-blue-50 border-blue-200 mb-6">
        <div className="card-body">
          <div className="flex items-start">
            <DocumentTextIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-blue-800 mb-2">Sobre a Estrutura DRE</h4>
              <ul className="space-y-1 text-blue-700">
                <li>• A ordem dos itens define a sequência de cálculo na DRE</li>
                <li>• Use fórmulas para cálculos automáticos (ex: posicao_1 - posicao_2)</li>
                <li>• Os tipos definem a cor e comportamento do item</li>
                <li>• A estrutura pode ser personalizada conforme necessidade</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Lista da Estrutura */}
      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            Estrutura da DRE ({estrutura.length} itens)
          </h3>
          {carregando && (
            <div className="loader h-6 w-6"></div>
          )}
        </div>
        
        <div className="card-body p-0">
          {estrutura.length > 0 ? (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th className="w-16">Ordem</th>
                    <th>Nome</th>
                    <th>Tipo</th>
                    <th>Fórmula</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {estrutura.map((item, index) => (
                    <tr key={item.id}>
                      <td>
                        <div className="flex items-center space-x-1">
                          <Bars3Icon className="h-5 w-5 text-gray-400 cursor-move" />
                          <span className="font-bold">{item.ordem}</span>
                          <div className="flex flex-col">
                            {index > 0 && (
                              <button
                                onClick={() => moverItem(item.id, 'up')}
                                className="text-gray-400 hover:text-gray-600"
                                title="Mover para cima"
                              >
                                <ArrowsUpDownIcon className="h-3 w-3 rotate-90" />
                              </button>
                            )}
                            {index < estrutura.length - 1 && (
                              <button
                                onClick={() => moverItem(item.id, 'down')}
                                className="text-gray-400 hover:text-gray-600"
                                title="Mover para baixo"
                              >
                                <ArrowsUpDownIcon className="h-3 w-3 -rotate-90" />
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center">
                          {getIconByTipo(item.tipo)}
                          <span className="ml-2 font-medium">{item.nome}</span>
                        </div>
                      </td>
                      <td>
                        <span className={getBadgeColorByTipo(item.tipo)}>
                          {getTipoLabel(item.tipo)}
                        </span>
                      </td>
                      <td>
                        <div className="max-w-xs">
                          <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                            {item.formula || 'Manual'}
                          </code>
                          {item.formula && (
                            <p className="text-xs text-gray-500 mt-1">
                              Referência: {item.formula.replace(/posicao_/g, 'Item ')}
                            </p>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="text-primary-600 hover:text-primary-900 p-1"
                            title="Editar"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="text-danger-600 hover:text-danger-900 p-1"
                            title="Excluir"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <CalculatorIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-lg font-medium">Estrutura DRE não configurada</p>
              <p className="text-sm mt-1">Configure a estrutura da DRE para gerar relatórios automáticos</p>
              <div className="mt-4 flex items-center justify-center gap-3">
                <button
                  onClick={carregarModeloPadrao}
                  className="btn-secondary"
                  disabled={carregandoModeloPadrao}
                >
                  <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                  {carregandoModeloPadrao ? 'Carregando...' : 'Carregar Modelo Padrao'}
                </button>
                <button
                  onClick={() => setShowModal(true)}
                  className="btn-primary"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Configurar Primeiro Item
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Item DRE */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">
                {editingId ? '✏️ Editar Item DRE' : '➕ Novo Item DRE'}
              </h3>
            </div>
            
            <form onSubmit={handleSubmit(onSubmit)} className="p-6">
              <div className="space-y-4">
                <div className="form-group">
                  <label className="form-label">Ordem *</label>
                  <input
                    type="number"
                    min="1"
                    {...register('ordem', { 
                      required: 'Ordem é obrigatória',
                      min: {
                        value: 1,
                        message: 'Ordem deve ser maior que zero'
                      }
                    })}
                    className="form-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Posição na sequência da DRE
                  </p>
                  {errors.ordem && (
                    <p className="form-error">{errors.ordem.message}</p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Nome *</label>
                  <input
                    type="text"
                    {...register('nome', { 
                      required: 'Nome é obrigatório',
                      minLength: {
                        value: 3,
                        message: 'Mínimo 3 caracteres'
                      }
                    })}
                    className="form-input"
                    placeholder="Ex: Receita Operacional Bruta"
                  />
                  {errors.nome && (
                    <p className="form-error">{errors.nome.message}</p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Tipo *</label>
                  <select
                    {...register('tipo', { required: 'Tipo é obrigatório' })}
                    className="form-select"
                  >
                    <option value="">Selecione...</option>
                    <option value="receita">Receita</option>
                    <option value="despesa">Despesa</option>
                    <option value="resultado">Resultado</option>
                  </select>
                  {errors.tipo && (
                    <p className="form-error">{errors.tipo.message}</p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Fórmula de Cálculo</label>
                  <input
                    type="text"
                    {...register('formula')}
                    className="form-input"
                    placeholder="Ex: posicao_1 - posicao_2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use posicao_X para referenciar outros itens (ex: posicao_1 - posicao_2)
                  </p>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    reset();
                    setEditingId(null);
                  }}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  {editingId ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DREEstrutura;
